import { Env } from '/@/Env';
import { AuthUser } from '@/modules/auth/models/AuthUser';
import { User } from '@/modules/user/models/User';
import { DefaultProperties } from '@/integrations/analytics/types/DefaultProperties';
import { IdentifyTraitsProperties } from '@/integrations/analytics/types/IdentifyTraitsProperties';
import { TrackProperties } from '@/integrations/analytics/types/TrackProperties';
import { UserProperties } from '@/integrations/analytics/types/UserProperties';
import { PageProperties } from '@/integrations/analytics/types/PageProperties';
import AnalyticsManager from '@/integrations/analytics/AnalyticsManager';
import MixpanelProvider from '@/integrations/analytics/providers/MixpanelProvider';
import Locale from '@/modules/user/Enum/Locale';

class AnalyticsService {
  user: AuthUser | User | null;
  groupId: string | number = '';

  private analyticsManager: typeof AnalyticsManager;
  private initialized: boolean = false;

  constructor() {
    this.user = null;
    this.analyticsManager = AnalyticsManager;
  }

  setUser(user: AuthUser | User | null): void {
    this.user = user;
    this.groupId =
      this.user && this.user instanceof AuthUser ? this.user.currentEstate?.id ?? '' : '';
  }

  environmentShouldTrack(): boolean {
    return (
      !window?.location.host.includes('localhost') &&
      !Env.isRunningUnitTest() &&
      !Env.isLocal() &&
      !Env.isRunningCypress()
    );
  }

  track(eventName: string, eventProps: Record<string, unknown>): void {
    if (!this.environmentShouldTrack()) {
      return;
    }

    const mergedProperties: Record<string, unknown> = {
      ...this.getTrackProperties(),
      ...eventProps
    };

    this.analyticsManager.track(eventName, mergedProperties);
  }

  page(
    name: string,
    path: string,
    referrer: string,
    properties: Record<string, unknown> = {},
    options: Record<string, unknown> = {}
  ): void {
    if (!this.environmentShouldTrack()) {
      return;
    }

    properties = { name, ...properties };
    const mergedProperties: Record<string, unknown> = {
      ...this.getPageProperties(path, referrer),
      ...properties
    };

    this.analyticsManager.page(name, mergedProperties, options);
  }

  init(user: AuthUser | null, includeAlias: boolean = false): void {
    if (!this.environmentShouldTrack()) {
      return;
    }

    if (!this.initialized) {
      this.analyticsManager.registerProvider(MixpanelProvider);

      this.initialized = true;
    }

    this.analyticsManager.init();

    if (!user) {
      return;
    }

    this.setUser(user);
    if (includeAlias) {
      // @ts-ignore
      this.alias(this.user!.id);
    }
    this.identify();
  }

  alias(userId: number): void {
    this.analyticsManager.alias(userId);
  }

  identify(): void {
    if (!this.environmentShouldTrack()) {
      return;
    }

    const traits: IdentifyTraitsProperties = this.getIdentifyTraits();

    // @ts-ignore
    this.analyticsManager.identify(this.user?.id, traits);
  }

  identifyWithEventProperties(eventIdentifyProps: Record<string, unknown>): void {
    if (!this.environmentShouldTrack()) {
      return;
    }

    const traits: IdentifyTraitsProperties = this.getIdentifyTraits();
    // @ts-ignore
    this.analyticsManager.identify(this.user?.id, { ...traits, ...eventIdentifyProps });
  }

  private getDefaultProperties(): DefaultProperties {
    const properties: Record<string, unknown> = this.user ? { email: this.user.email } : {};

    return this.user instanceof AuthUser && this.user?.currentEstate
      ? {
          ...properties,
          estateName: this.user.currentEstate.contact.fullName,
          estateId: this.user.currentEstate.id,
          jurisdiction: this.user.currentEstate.jurisdiction.name,
          product: this.user.currentEstate.displayProductType,
          isDemo: this.user.currentEstate.isDemo,
          billingPlan: this.user.currentEstate?.billing?.planId ?? '',
          referralPartner: this.user.currentEstate?.company?.name ?? ''
        }
      : properties;
  }

  reset(): void {
    this.setUser(null);
    this.analyticsManager.reset();
  }

  private getIdentifyTraits(): IdentifyTraitsProperties {
    const identifyTraits: Record<string, unknown> = this.user
      ? {
          email: this.user.email,
          name: this.user.contact.fullName,
          createdAt: this.user.createdAt,
          language: Locale.getByKey(this.user.locale)?.value ?? '',
          daysSinceAccountCreated: this.user.daysSinceAccountCreation
        }
      : {};
    return {
      href: window.location.href,
      ...identifyTraits,
      ...this.getDefaultProperties()
    };
  }

  getTrackProperties(): TrackProperties {
    return {
      search: window.location.search,
      groupId: this.groupId || '',
      href: window.location.href,
      ...this.getDefaultProperties()
    };
  }

  private getPageProperties(path: string, referrer: string): PageProperties {
    return {
      path,
      search: window.location.search,
      url: path,
      title: document.title,
      referrer,
      ...this.getUserProperties(),
      ...this.getDefaultProperties()
    };
  }

  private getUserProperties(): UserProperties | object {
    return this.user
      ? {
          userId: this.user.id,
          userName: this.user.name,
          userRole: this.user.estateAccount?.roleName ?? this.user.roleName
        }
      : {};
  }

  encodeTrackingUrlValue(trackingProperties: Record<string, unknown>): string {
    return btoa(JSON.stringify(trackingProperties));
  }

  decodeTrackingUrlValue(encodedValue: string): Record<string, unknown> {
    return JSON.parse(atob(encodedValue));
  }
}

export default new AnalyticsService();
